import { addDays, format, isSameDay, parseISO, startOfDay } from 'date-fns';
import {
  evaluateTripJoinability,
  getJoinCutoffHours,
  joinRejectionMessage,
  parseDepartureInstant,
} from '../config/sharedTransferRules';

const MINUTES_PER_DAY = 24 * 60;
const DEFAULT_DURATION_MIN = 120;

export function parseTimeToMinutes(timeStr) {
  if (!timeStr) return 8 * 60;
  const [h, m] = timeStr.split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
}

export function getTripDurationMinutes(trip) {
  const start = parseTimeToMinutes(trip.time_window_start);
  const end = parseTimeToMinutes(trip.time_window_end);
  const span = end > start ? end - start : DEFAULT_DURATION_MIN;
  return Math.max(span, 60);
}

export function buildDayRange(numDays, fromDate = new Date()) {
  const start = startOfDay(fromDate);
  return Array.from({ length: numDays }, (_, i) => addDays(start, i));
}

export function toDateKey(date) {
  return format(date, 'yyyy-MM-dd');
}

export function groupTripsByDate(trips) {
  return trips.reduce((acc, trip) => {
    const key = trip.travel_date;
    if (!key) return acc;
    if (!acc[key]) acc[key] = [];
    acc[key].push(trip);
    return acc;
  }, {});
}

export function sortTripsByDeparture(trips) {
  return [...trips].sort((a, b) => {
    const aMin = parseTimeToMinutes(a.time_window_start);
    const bMin = parseTimeToMinutes(b.time_window_start);
    if (aMin !== bMin) return aMin - bMin;
    return (a.pickup_location || '').localeCompare(b.pickup_location || '');
  });
}

export function getTripTimelineStyle(trip, timelineHeightPx) {
  const startMin = parseTimeToMinutes(trip.time_window_start);
  const durationMin = getTripDurationMinutes(trip);
  const topPx = (startMin / MINUTES_PER_DAY) * timelineHeightPx;
  const minChipPx = timelineHeightPx <= 220 ? 26 : 38;
  const heightPx = Math.max((durationMin / MINUTES_PER_DAY) * timelineHeightPx, minChipPx);
  return { topPx, heightPx };
}

export function isTripOnDate(trip, date) {
  if (!trip.travel_date) return false;
  try {
    return isSameDay(parseISO(trip.travel_date), date);
  } catch {
    return trip.travel_date === toDateKey(date);
  }
}

const ROUTE_COLORS = {
  Nairobi: { chip: 'bg-emerald-600/90 border-emerald-500', dot: 'bg-emerald-500' },
  Arusha: { chip: 'bg-sky-600/90 border-sky-500', dot: 'bg-sky-500' },
  Moshi: { chip: 'bg-amber-600/90 border-amber-500', dot: 'bg-amber-500' },
};

export function getRouteStyle(pickupLocation = '') {
  const city = ['Nairobi', 'Arusha', 'Moshi'].find((c) =>
    pickupLocation.toLowerCase().includes(c.toLowerCase())
  );
  return ROUTE_COLORS[city] || { chip: 'bg-stone-600/90 border-stone-500', dot: 'bg-stone-500' };
}

export function abbreviateLocation(name = '', maxLen = 12) {
  const cleaned = name.replace(/International Airport/i, 'Airport');
  if (cleaned.length <= maxLen) return cleaned;
  return cleaned.slice(0, maxLen) + '…';
}

const CITY_SHORT_CODES = {
  Nairobi: 'NBO',
  Arusha: 'ARU',
  Moshi: 'MOS',
};

export function getLocationShortCode(location = '') {
  const city = ['Nairobi', 'Arusha', 'Moshi'].find((c) =>
    location.toLowerCase().includes(c.toLowerCase())
  );
  if (city) return CITY_SHORT_CODES[city];
  const trimmed = location.trim();
  if (!trimmed) return '—';
  if (trimmed.length <= 4) return trimmed.toUpperCase();
  return trimmed.slice(0, 3).toUpperCase();
}

export function formatRouteLabel(trip, { compact = false } = {}) {
  const pickup = trip.pickup_location || '';
  const dropoff = trip.dropoff_location || '';
  if (compact) {
    return `${getLocationShortCode(pickup)}→${getLocationShortCode(dropoff)}`;
  }
  return `${abbreviateLocation(pickup, 10)} → ${abbreviateLocation(dropoff, 10)}`;
}

/** Short departure time for chip metadata, e.g. "10:00" */
export function formatChipDepartureTime(startTime) {
  if (!startTime) return 'TBD';
  const [h, m] = startTime.split(':');
  const hour = parseInt(h, 10);
  const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return `${displayHour}:${m || '00'}`;
}

export function formatChipMetaLine(trip, { seatsLeft, isJoined = false } = {}) {
  const time = formatChipDepartureTime(trip.time_window_start);
  const seats = seatsLeft > 0 ? `${seatsLeft} left` : 'Full';
  let line = `${time} · ${seats}`;
  if (isJoined) line += ' · Joined';
  return line;
}

export const TIMELINE_LABELS = [
  { label: '12 AM', minutes: 0 },
  { label: '6 AM', minutes: 6 * 60 },
  { label: '12 PM', minutes: 12 * 60 },
  { label: '6 PM', minutes: 18 * 60 },
];

export function getTripCapacity(trip) {
  return trip.max_participants ?? trip.max_seats ?? 6;
}

export function getSeatsRemaining(trip) {
  const capacity = getTripCapacity(trip);
  const filled = trip.seats_filled ?? 0;
  return Math.max(0, capacity - filled);
}

export function isTripFull(trip) {
  return getSeatsRemaining(trip) <= 0;
}

/**
 * Mirrors join eligibility from TripCard (creator/joined checks done by caller).
 */
export function getJoinDeadlineInfo(trip) {
  if (!trip?.travel_date) return null;

  const start = trip.time_window_start || '00:00:00';
  const departureTime = parseDepartureInstant(trip.travel_date, start);
  if (!departureTime) return null;

  const cutoffHours = getJoinCutoffHours(trip);
  const joinCloseAt = new Date(
    departureTime.getTime() - cutoffHours * 60 * 60 * 1000
  );
  const msUntilClose = joinCloseAt.getTime() - Date.now();
  const hoursUntilClose = msUntilClose / (1000 * 60 * 60);

  return {
    joinCloseAt,
    departureTime,
    isClosed: msUntilClose <= 0,
    hoursUntilClose,
  };
}

export function formatJoinDeadlineLabel(trip) {
  const info = getJoinDeadlineInfo(trip);
  if (!info) return null;

  if (info.isClosed) return 'Join closed';

  const { hoursUntilClose, joinCloseAt } = info;

  if (hoursUntilClose > 48) {
    return `Closes ${format(joinCloseAt, 'MMM d')} · ${format(joinCloseAt, 'h a')}`;
  }

  if (hoursUntilClose >= 1) {
    return `Join closes in ${Math.ceil(hoursUntilClose)}h`;
  }

  const minutes = Math.ceil(hoursUntilClose * 60);
  if (minutes > 0) return `Join closes in ${minutes}m`;

  return 'Join closed';
}

/**
 * Single join-eligibility source for browse detail UI — countdown only when joinable.
 */
export function getJoinEligibility(trip, { isJoined = false, isCreator = false } = {}) {
  if (isJoined) {
    return { joinable: false, deadlineLabel: 'Already joined' };
  }
  if (isCreator) {
    return { joinable: false, deadlineLabel: 'Your trip' };
  }
  if (isTripFull(trip)) {
    return { joinable: false, deadlineLabel: 'Trip full' };
  }
  if (trip.status !== 'open' && trip.status !== 'confirmed') {
    return { joinable: false, deadlineLabel: 'Join unavailable' };
  }

  const joinability = evaluateTripJoinability(trip);
  if (!joinability.ok) {
    const message = joinRejectionMessage(joinability.reason);
    return {
      joinable: false,
      deadlineLabel: message || 'Join closed',
    };
  }

  return {
    joinable: true,
    deadlineLabel: formatJoinDeadlineLabel(trip),
  };
}

export function canJoinTrip(trip, { isJoined = false, isCreator = false } = {}) {
  if (isJoined || isCreator) return false;
  if (isTripFull(trip)) return false;
  if (trip.status !== 'open' && trip.status !== 'confirmed') return false;
  return evaluateTripJoinability(trip).ok;
}

/**
 * Assigns horizontal lanes for overlapping trips on the same day column.
 */
export function layoutOverlappingTrips(trips, timelineHeightPx) {
  const sorted = sortTripsByDeparture(trips);
  const laneEnds = [];
  const placed = [];

  for (const trip of sorted) {
    const start = parseTimeToMinutes(trip.time_window_start);
    const end = start + getTripDurationMinutes(trip);
    let lane = 0;
    while (lane < laneEnds.length && start < laneEnds[lane]) lane++;
    if (lane === laneEnds.length) laneEnds.push(end);
    else laneEnds[lane] = Math.max(laneEnds[lane], end);

    const { topPx, heightPx } = getTripTimelineStyle(trip, timelineHeightPx);
    placed.push({ trip, lane, topPx, heightPx });
  }

  const maxLanes = Math.max(1, laneEnds.length);
  const gapPct = 2;
  const widthPct = (100 - gapPct * (maxLanes - 1)) / maxLanes;

  return placed.map(({ trip, lane, topPx, heightPx }) => ({
    trip,
    topPx,
    heightPx,
    leftPct: lane * (widthPct + gapPct),
    widthPct,
  }));
}
